import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Textarea } from '@jaylabs/ui';
import Logo from "../components/Logo";
import AppFooter from "../components/AppFooter";
import ChatSidebar from "../components/ChatSidebar";
import HeaderActionsBar from "../components/HeaderActionsBar";
import { API_URL } from "../utils/apiUrl";
import { getProjects } from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dueDateRange, setDueDateRange] = useState(7);
  const [projects, setProjects] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');

  // Dropdown open states
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [bgMenuOpen, setBgMenuOpen] = useState(false);
  const addRef = useRef(null);
  const viewRef = useRef(null);
  const bgRef = useRef(null);

  const backgroundOptions = [
    { label: 'Blue Hex', value: '/backgrounds/Background-2-1024x682.jpg' },
    { label: 'Soft Gradient', value: '/backgrounds/Background-3-1024x682.jpg' },
    { label: 'Electric Blue', value: '/backgrounds/Background-4-1024x682.jpg' },
    { label: 'Deep Purple', value: '/backgrounds/Background-5-1024x682.jpg' },
    { label: 'Calm Waves', value: '/backgrounds/Background-6-1024x682.jpg' },
    { label: 'Vibrant Mesh', value: '/backgrounds/Background-7-1024x682.jpg' },
    { label: 'Cool Grid', value: '/backgrounds/Background-8-1024x682.jpg' },
    { label: 'Bright Blocks', value: '/backgrounds/Background-10-1024x682.jpg' },
    { label: 'Soft Blue', value: '/backgrounds/Background-11-1024x683.jpg' }
  ];
  const [bgChoice, setBgChoice] = useState(
    localStorage.getItem('dashboardBg') || backgroundOptions[0].value
  );

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (addRef.current && !addRef.current.contains(e.target)) setAddMenuOpen(false);
      if (viewRef.current && !viewRef.current.contains(e.target)) setViewMenuOpen(false);
      if (bgRef.current && !bgRef.current.contains(e.target)) setBgMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTasks(data.tasks);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await getProjects(false);
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/inbox?unreadOnly=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchUnreadCount();
    fetchProjects();
  }, [fetchTasks, fetchUnreadCount, fetchProjects]);

  useEffect(() => {
    localStorage.setItem('dashboardBg', bgChoice);
  }, [bgChoice]);

  useEffect(() => {
    if (chatOpen) setUnreadCount(0);
  }, [chatOpen]);

  // Task statistics
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.is_completed).length;
  const activeTasks = totalTasks - completedTasks;

  const priorityRank = (priority = 'normal') => {
    const val = String(priority || '').toLowerCase();
    if (val === 'critical') return 4;
    if (val === 'high') return 3;
    if (val === 'medium') return 2;
    if (val === 'low') return 1;
    return 0;
  };

  // Upcoming due dates
  const now = new Date();
  const nextDate = new Date(now.getTime() + dueDateRange * 24 * 60 * 60 * 1000);
  const upcomingTasks = tasks
    .filter(task => {
      if (!task.due_date || task.is_completed) return false;
      const dueDate = new Date(task.due_date);
      return dueDate >= now && dueDate <= nextDate;
    })
    .sort((a, b) => {
      const prDiff = priorityRank(b.priority) - priorityRank(a.priority);
      if (prDiff !== 0) return prDiff;
      return new Date(a.due_date) - new Date(b.due_date);
    });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const submitFeedback = async () => {
    if (!feedbackMessage.trim()) {
      setFeedbackStatus('Please enter feedback before submitting.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: feedbackMessage })
      });
      if (response.ok) {
        setFeedbackMessage('');
        setFeedbackStatus('Thanks for your feedback!');
      } else {
        const data = await response.json();
        setFeedbackStatus(data.message || 'Failed to submit feedback.');
      }
    } catch (err) {
      setFeedbackStatus('Error connecting to server.');
    }
  };

  const hideFriends = localStorage.getItem('hideFriends') === 'true';

  return (
    <div
      className="dd-dashboard-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${bgChoice})`
      }}
    >
      <div className="dd-container" style={{ paddingTop: '4rem' }}>
        {/* Top Bar */}
        <div className="dd-hstack dd-mb-6" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Logo size="xxl" />
          <HeaderActionsBar>
            {/* Create Menu */}
            <div className="dd-dropdown" ref={addRef}>
              <button
                className="dd-dropdown-trigger"
                aria-label="Add"
                title="Add"
                onClick={() => { setAddMenuOpen(!addMenuOpen); setViewMenuOpen(false); setBgMenuOpen(false); }}
              >
                <span className="dd-dropdown-trigger-gradient">+</span>
              </button>
              {addMenuOpen && (
                <div className="dd-dropdown-menu">
                  <button className="dd-dropdown-item" onClick={() => { navigate('/tasks/create'); setAddMenuOpen(false); }}>
                    + Add Task
                  </button>
                  <button className="dd-dropdown-item" onClick={() => { navigate('/projects'); setAddMenuOpen(false); }}>
                    Add Project
                  </button>
                  {!hideFriends && (
                    <button className="dd-dropdown-item" onClick={() => { navigate('/friends'); setAddMenuOpen(false); }}>
                      Add Friend
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* View Menu */}
            <div className="dd-dropdown" ref={viewRef}>
              <button
                className="dd-dropdown-trigger"
                aria-label="View"
                title="View"
                onClick={() => { setViewMenuOpen(!viewMenuOpen); setAddMenuOpen(false); setBgMenuOpen(false); }}
              >
                <span aria-hidden="true">&#128065;</span>
              </button>
              {viewMenuOpen && (
                <div className="dd-dropdown-menu">
                  <button className="dd-dropdown-item" onClick={() => { navigate('/tasks'); setViewMenuOpen(false); }}>
                    View Tasks
                  </button>
                  <button className="dd-dropdown-item" onClick={() => { navigate('/projects'); setViewMenuOpen(false); }}>
                    View Projects
                  </button>
                  {!hideFriends && (
                    <button className="dd-dropdown-item" onClick={() => { navigate('/friends'); setViewMenuOpen(false); }}>
                      View Friends
                    </button>
                  )}
                  <button className="dd-dropdown-item" onClick={() => { navigate('/settings'); setViewMenuOpen(false); }}>
                    Task Settings
                  </button>
                </div>
              )}
            </div>

            {/* Background chooser */}
            <div className="dd-dropdown" ref={bgRef}>
              <button
                className="dd-dropdown-trigger"
                aria-label="Backgrounds"
                title="Backgrounds"
                onClick={() => { setBgMenuOpen(!bgMenuOpen); setAddMenuOpen(false); setViewMenuOpen(false); }}
              >
                <span aria-hidden="true">&#127912;</span>
              </button>
              {bgMenuOpen && (
                <div className="dd-dropdown-menu">
                  {backgroundOptions.map(bg => (
                    <button
                      key={bg.value}
                      className="dd-dropdown-item"
                      onClick={() => { setBgChoice(bg.value); setBgMenuOpen(false); }}
                    >
                      {bg.label} {bgChoice === bg.value ? ' ✓' : ''}
                    </button>
                  ))}
                  <div className="dd-dropdown-separator" />
                  <button className="dd-dropdown-item" onClick={() => { setBgChoice(backgroundOptions[0].value); setBgMenuOpen(false); }}>
                    Reset to default
                  </button>
                </div>
              )}
            </div>

            {!hideFriends && (
              <Button
                variant="primary"
                size="lg"
                onClick={() => setChatOpen(true)}
              >
                Chat{unreadCount > 0 ? ` (${unreadCount})` : ''}
              </Button>
            )}
          </HeaderActionsBar>
        </div>

        {/* Header */}
        <h1 className="dd-heading-xl dd-mb-10">Welcome to Your Dashboard!</h1>

        {/* Chat Sidebar */}
        <ChatSidebar isOpen={chatOpen} onClose={() => setChatOpen(false)} />

        {/* Task Statistics */}
        {loading ? (
          <div className="dd-text-center dd-py-8">
            <div className="dd-spinner dd-spinner-xl"></div>
            <p className="dd-text-lg dd-text-secondary dd-mt-4">Loading...</p>
          </div>
        ) : (
          <>
            {/* Stats Row */}
            <div className="dd-stats-grid dd-mb-6">
              <div className="dd-stat-card dd-stat-card-purple">
                <p className="dd-stat-label">Total Points</p>
                <p className="dd-stat-value dd-stat-value-purple">{user?.total_points}</p>
                <p className="dd-stat-level">Level {user?.level || '1'}</p>
              </div>
              <button
                className="dd-stat-card dd-stat-card-blue dd-stat-card-clickable"
                onClick={() => navigate('/tasks')}
                aria-label={`Total tasks: ${totalTasks}. Click to view all tasks.`}
              >
                <p className="dd-stat-label">Total Tasks</p>
                <p className="dd-stat-value dd-stat-value-blue">{totalTasks}</p>
                <p className="dd-stat-sub">All tasks</p>
              </button>
              <button
                className="dd-stat-card dd-stat-card-orange dd-stat-card-clickable"
                onClick={() => navigate('/tasks', { state: { showCompleted: false } })}
                aria-label={`Active tasks: ${activeTasks}. Click to view active tasks.`}
              >
                <p className="dd-stat-label">Active Tasks</p>
                <p className="dd-stat-value dd-stat-value-orange">{activeTasks}</p>
                <p className="dd-stat-sub">To complete</p>
              </button>
              <button
                className="dd-stat-card dd-stat-card-green dd-stat-card-clickable"
                onClick={() => navigate('/tasks', { state: { showCompleted: true } })}
                aria-label={`Completed tasks: ${completedTasks}. Click to view completed tasks.`}
              >
                <p className="dd-stat-label">Completed</p>
                <p className="dd-stat-value dd-stat-value-green">{completedTasks}</p>
                <p className="dd-stat-sub">Finished!</p>
              </button>
            </div>

            {/* Two-column body */}
            <div className="dd-dashboard-body">

              {/* Left column */}
              <div className="dd-dashboard-main">

                {/* Upcoming Due Dates */}
                <div className="dd-card dd-mb-6">
                  <div className="dd-hstack dd-mb-4" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h4 className="dd-heading-md">Upcoming Due Dates</h4>
                    <div className="dd-hstack dd-gap-2">
                      {[7, 14, 21].map(range => (
                        <button
                          key={range}
                          className={`dd-range-btn ${dueDateRange === range ? 'dd-range-btn-active' : ''}`}
                          onClick={() => setDueDateRange(range)}
                        >
                          {range}d
                        </button>
                      ))}
                    </div>
                  </div>
                  {upcomingTasks.length === 0 ? (
                    <p className="dd-text-secondary dd-text-center dd-py-4">
                      No tasks due in the next {dueDateRange} days
                    </p>
                  ) : (
                    <div className="dd-vstack dd-gap-3">
                      {upcomingTasks.map(task => (
                        <div key={task.id} className="dd-upcoming-task">
                          <div className="dd-hstack dd-gap-3" style={{ flex: 1, flexWrap: 'wrap' }}>
                            <span
                              className="dd-upcoming-task-title"
                              onClick={() => navigate('/tasks', { state: { openTaskId: task.id } })}
                              title="Open task"
                            >
                              {task.title}
                            </span>
                            {task.category && <Badge variant="primary" size="sm">{task.category}</Badge>}
                          </div>
                          <div className="dd-hstack dd-gap-2">
                            <Badge variant="warning" size="sm">Due {formatDate(task.due_date)}</Badge>
                            <Badge variant="info" size="sm">{task.points} pts</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Changes & Coming Soon */}
                <div className="dd-info-grid dd-mb-6">
                  <div className="dd-info-card dd-info-card-green">
                    <h3>Recent Changes</h3>
                    <ul className="dd-info-list">
                      <li>Auth pages redesigned with consistent cards & components</li>
                      <li>Settings page now uses tabbed layout (Profile / Security / Appearance / Task Settings)</li>
                      <li>Register form validates fields inline as you type</li>
                      <li>Friends page uses toast notifications instead of alerts</li>
                      <li>Chat polling slows to 30s when tab is in background</li>
                      <li>All touch targets enlarged to WCAG 44px minimum</li>
                    </ul>
                  </div>
                  <div className="dd-info-card dd-info-card-blue">
                    <h3>Coming Soon</h3>
                    <ul className="dd-info-list">
                      <li>Leaderboards with filters</li>
                      <li>Analytics for points and levels</li>
                      <li>Goal/OKR tracking</li>
                      <li>Self-kindness task generator</li>
                      <li>Smarter notifications</li>
                      <li>Mobile companions</li>
                    </ul>
                  </div>
                </div>

                {/* Feedback box */}
                <div className="dd-info-card dd-info-card-purple dd-mb-6">
                  <h3>Share Feedback</h3>
                  <p className="dd-text-secondary dd-mb-3">
                    Tell us what to improve or celebrate. Your note goes straight to the admin inbox.
                  </p>
                  <Textarea
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Drop your feedback here..."
                    rows={3}
                  />
                  <div className="dd-hstack dd-mt-3" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <Button variant="primary" onClick={submitFeedback}>
                      Send feedback
                    </Button>
                    {feedbackStatus && (
                      <span className="dd-feedback-status">{feedbackStatus}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right sidebar */}
              <div className="dd-dashboard-sidebar">

                {/* Projects */}
                <div className="dd-card dd-mb-6">
                  <div className="dd-hstack dd-mb-4" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 className="dd-heading-md">Projects</h4>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}>
                      View all
                    </Button>
                  </div>
                  {projects.length === 0 ? (
                    <div className="dd-text-center dd-py-4">
                      <p className="dd-text-secondary dd-mb-3">No projects yet</p>
                      <Button variant="primary" size="sm" onClick={() => navigate('/projects')}>
                        Create project
                      </Button>
                    </div>
                  ) : (
                    <div className="dd-vstack dd-gap-2">
                      {projects.map(project => (
                        <div
                          key={project.id}
                          className="dd-project-item"
                          onClick={() => navigate('/tasks', { state: { projectId: project.id } })}
                        >
                          <span
                            className="dd-project-dot"
                            style={{ backgroundColor: project.color || '#3b82f6' }}
                          />
                          <span className="dd-project-name">{project.name}</span>
                          <span className="dd-project-count">
                            {project.task_count ?? 0}
                          </span>
                        </div>
                      ))}
                      <button
                        className="dd-project-add-btn"
                        onClick={() => navigate('/projects')}
                      >
                        + New project
                      </button>
                    </div>
                  )}
                </div>

                {/* Quick stats sidebar card */}
                <div className="dd-card">
                  <h4 className="dd-heading-md dd-mb-4">Progress</h4>
                  <div className="dd-vstack dd-gap-3">
                    <div>
                      <div className="dd-hstack dd-mb-1" style={{ justifyContent: 'space-between' }}>
                        <span className="dd-text-secondary" style={{ fontSize: '0.875rem' }}>Completion rate</span>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%
                        </span>
                      </div>
                      <div className="dd-progress-track">
                        <div
                          className="dd-progress-bar"
                          style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="dd-hstack" style={{ justifyContent: 'space-between' }}>
                      <span className="dd-text-secondary" style={{ fontSize: '0.875rem' }}>Badge</span>
                      <Badge variant="primary" size="sm">{user?.badge || 'Novice'}</Badge>
                    </div>
                    <div className="dd-hstack" style={{ justifyContent: 'space-between' }}>
                      <span className="dd-text-secondary" style={{ fontSize: '0.875rem' }}>Projects</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{projects.length}</span>
                    </div>
                    <div className="dd-hstack" style={{ justifyContent: 'space-between' }}>
                      <span className="dd-text-secondary" style={{ fontSize: '0.875rem' }}>Due soon</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{upcomingTasks.length}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </>
        )}

        <AppFooter />
      </div>
    </div>
  );
}

export default Dashboard;
